import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Tag } from "lucide-react";

export function CategoriesDialog({ open, onOpenChange, onChanged }) {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}

  const load = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!newName.trim()) return;
    await api.post("/categories", { name: newName.trim(), order: cats.length });
    setNewName("");
    await load();
    onChanged?.();
    toast.success("Category added");
  };

  const saveEdit = async () => {
    if (!editing.name.trim()) return;
    await api.put(`/categories/${editing.id}`, { name: editing.name.trim(), order: 0 });
    setEditing(null);
    await load();
    onChanged?.();
    toast.success("Category renamed");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category? Items will become uncategorized.")) return;
    await api.delete(`/categories/${id}`);
    await load();
    onChanged?.();
    toast.success("Category deleted");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-white/15 rounded-none max-w-md" data-testid="categories-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl flex items-center gap-2"><Tag size={20} /> Categories</DialogTitle>
          <DialogDescription className="sr-only">Create, rename and delete categories used to group items.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-white/10 bg-[#121212] px-3 py-2" data-testid={`category-row-${c.id}`}>
              {editing?.id === c.id ? (
                <>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-8 focus-visible:ring-[#FF3B30]" autoFocus />
                  <button onClick={saveEdit} className="text-[#FF3B30] p-1"><Check size={16} /></button>
                  <button onClick={() => setEditing(null)} className="text-zinc-400 p-1"><X size={16} /></button>
                </>
              ) : (
                <>
                  <span className="flex-grow text-sm">{c.name}</span>
                  <button data-testid={`edit-category-${c.id}`} onClick={() => setEditing({ id: c.id, name: c.name })} className="text-zinc-400 hover:text-[#FF3B30] p-1"><Pencil size={14} /></button>
                  <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c.id)} className="text-zinc-400 hover:text-red-500 p-1"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
          {cats.length === 0 && <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-4 text-center">No categories yet</p>}
        </div>
        <div className="flex gap-2 pt-2 border-t border-white/10">
          <Input data-testid="new-category-input" value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name"
            className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" />
          <button data-testid="add-category-btn" onClick={add}
            className="flex items-center gap-1 bg-white text-black font-bold px-4 uppercase tracking-wider text-xs hover:bg-[#FF3B30] hover:text-white transition-colors">
            <Plus size={16} /> Add
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
